import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { cn } from "@/lib/utils";
import axios from "axios";
import { Trophy, Users, Calendar, Wand2, FileText, Check, ChevronRight, FileSpreadsheet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "season", num: 1, icon: Trophy, title: "Seizoen aanmaken", desc: "Stel je club, team en seizoensdelen in", link: "/season" },
  { key: "players", num: 2, icon: Users, title: "Spelers toevoegen", desc: "Voeg je teamspelers toe", link: "/players" },
  { key: "matches", num: 3, icon: Calendar, title: "Wedstrijden invoeren", desc: "Plan de wedstrijden voor het seizoen", link: "/matches" },
  { key: "planning", num: 4, icon: Wand2, title: "Planning genereren", desc: "Verdeel taken automatisch over spelers", link: "/planning" },
  { key: "overview", num: 5, icon: FileText, title: "Overzicht bekijken", desc: "Bekijk en deel het complete overzicht", link: "/overview" },
];

// Alternatieve tekst voor stap 4 wanneer de planning al is geïmporteerd
// (bijv. via Excel-import) — dan hoeft er niets meer gegenereerd te worden.
const PLANNING_IMPORTED = {
  title: "Planning bekijken",
  desc: "Je geïmporteerde planning is klaar — bekijk en pas aan waar nodig",
};

export const GetStarted = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/status`);
        setStatus(res.data);
      } catch {
        setStatus({ complete: false, current_step: 1, steps: { season: false, players: false, matches: false, planning: false, overview: false }, messages: {}, imported_from_excel: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading || !status) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const completedCount = Object.values(status.steps).filter(Boolean).length;
  const messages = status.messages || {};

  return (
    <div data-testid="get-started-page" className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Aan de slag
        </h1>
        <p className="text-slate-500 mt-2">
          Volg de stappen hieronder om je team in te richten
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2" data-testid="onboarding-progress">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-slate-900 rounded-full transition-all duration-500"
            style={{ width: `${(completedCount / 5) * 100}%` }}
          />
        </div>
        <span className="text-sm font-medium text-slate-500">{completedCount}/5</span>
      </div>

      {/* Excel Import shortcut — always available so managers can bulk-import
          extra data or re-run the flow after onboarding is complete. */}
      <Card
        className="border-slate-200 rounded-xl cursor-pointer hover:border-slate-900 hover:shadow-sm transition-all"
        onClick={() => navigate("/import-excel")}
        data-testid="excel-import-card"
      >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Sneller starten</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">Importeer je Excel-planning</p>
              <p className="text-xs text-slate-500">Upload je bestaande sheet — Sportly vult spelers, wedstrijden, activiteiten en planning automatisch in</p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0 text-slate-400" />
          </CardContent>
        </Card>

      {/* Steps */}
      <div className="space-y-3">
        {STEPS.map((step) => {
          const done = status.steps[step.key];
          const isCurrent = step.num === status.current_step;
          const message = messages[step.key];
          const Icon = step.icon;
          // Stap 4 krijgt andere kop-tekst wanneer alles al is geïmporteerd
          // (matches + activities + plannings bestaan).
          const isImportedPlanning = step.key === "planning" && status.imported_from_excel;
          const title = isImportedPlanning ? PLANNING_IMPORTED.title : step.title;
          const desc = isImportedPlanning ? PLANNING_IMPORTED.desc : step.desc;

          return (
            <Card
              key={step.key}
              className={cn(
                "border rounded-xl transition-all cursor-pointer",
                done
                  ? "border-emerald-200 bg-emerald-50/50"
                  : isCurrent
                  ? "border-slate-900 bg-white shadow-sm"
                  : "border-slate-200 bg-white opacity-60"
              )}
              onClick={() => {
                if (done || isCurrent) navigate(step.link);
              }}
              data-testid={`step-${step.key}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div
                  className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                    done
                      ? "bg-emerald-500"
                      : isCurrent
                      ? "bg-slate-900"
                      : "bg-slate-200"
                  )}
                >
                  {done ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className={cn("w-5 h-5", isCurrent ? "text-white" : "text-slate-400")} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Stap {step.num}</span>
                    {done && <span className="text-xs text-emerald-600 font-medium">Voltooid</span>}
                  </div>
                  <p className={cn("text-sm font-semibold", done ? "text-emerald-900" : "text-slate-900")}>
                    {title}
                  </p>
                  <p className="text-xs text-slate-500">{desc}</p>
                  {!done && message && (
                    <p className="text-xs text-amber-600 mt-1" data-testid={`msg-${step.key}`}>
                      {message}
                    </p>
                  )}
                </div>
                {(done || isCurrent) && (
                  <ChevronRight className={cn("w-5 h-5 flex-shrink-0", done ? "text-emerald-400" : "text-slate-400")} />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* CTA for current step */}
      {!status.complete && status.current_step <= 5 && (
        <Button
          className="w-full bg-slate-900 hover:bg-slate-800 h-11"
          onClick={() => navigate(STEPS[status.current_step - 1].link)}
          data-testid="continue-btn"
        >
          {status.current_step === 1
            ? "Begin met seizoen aanmaken"
            : `Ga verder met stap ${status.current_step}: ${STEPS[status.current_step - 1].title}`}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
};
